import { ReactNode, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Login } from './Login';
import { SystemStatusHeader, type SystemStatusCounts } from './SystemStatusHeader';
import { useHealth } from '../hooks/useHealth';
import { useAuth } from '../hooks/useAuth';
import { isVideoEnabled } from '../utils/api';
import { GenerateScanModal } from '../video/components/GenerateScanModal';
import { useVideoHealth } from '../video/hooks/useVideoHealth';

/**
 * Window event dispatched when a video scan is submitted from the
 * AppShell-level modal. The video Live Scans page listens for this and
 * forces an immediate refresh so the user sees their freshly-submitted
 * job without waiting up to 3s for the next poll tick.
 */
export const VIDEO_SCAN_SUBMITTED_EVENT = 'video:scan-submitted';

const GITHUB_REPO_URL = 'https://github.com/UVeye/360_lightning';

type AppMode = 'lightning' | 'video';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

const lightningNavItems: NavItem[] = [
  {
    to: '/lightning/overview',
    label: 'Overview',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    to: '/lightning/live',
    label: 'Live Scans',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    to: '/lightning/history',
    label: 'History',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: '/lightning/stats',
    label: 'Statistics',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    to: '/lightning/settings',
    label: 'Settings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const videoNavItems: NavItem[] = [
  {
    to: '/video/overview',
    label: 'Overview',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    to: '/video/live',
    label: 'Live Scans',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    to: '/video/history',
    label: 'History',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: '/video/stats',
    label: 'Statistics',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    to: '/video/settings',
    label: 'Settings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

function detectMode(pathname: string): AppMode {
  // Anything under `/video/*` puts the shell into video mode; everything
  // else (including legacy paths still in flight before the redirect
  // settles) shows the lightning navigation.
  return pathname.startsWith('/video') ? 'video' : 'lightning';
}

interface ModeRailProps {
  mode: AppMode;
  videoVisible: boolean;
  homeLink: string;
}

interface ModeRailItem {
  to: string;
  label: string;
  modeId: AppMode;
  icon: ReactNode;
}

const modeRailItems: ModeRailItem[] = [
  {
    to: '/lightning/overview',
    label: 'Lightning',
    modeId: 'lightning',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    to: '/video/overview',
    label: '360 Video',
    modeId: 'video',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
  },
];

function ModeRail({ mode, videoVisible, homeLink }: ModeRailProps) {
  // Far-left rail that switches the whole app between the Lightning and
  // 360 Video mini-apps. The `videoVisible` flag is only false in
  // dev-mode builds where the developer didn't set `VITE_VIDEO_API_BASE`
  // in their `.env`; in that case we hide the video entry so the SPA
  // doesn't surface a link that would 502.
  const items = visibleModeRailItems(videoVisible);
  const appTitle = mode === 'video' ? '360 Video' : '360 Lightning';

  return (
    <aside
      className="hidden md:flex w-20 shrink-0 flex-col items-center py-4 border-r border-indigo-600 bg-indigo-500 sticky top-0 h-screen self-start z-30"
      aria-label="App mode"
    >
      <Link
        to={homeLink}
        className="w-10 h-10 rounded-xl bg-white/15 ring-1 ring-white/30 flex items-center justify-center shadow-sm hover:bg-white/25 hover:shadow-md transition-all duration-200"
        title={appTitle}
      >
        <img src="/logo.png" alt={appTitle} className="w-6 h-6 object-contain" />
      </Link>
      <div className="w-10 h-px bg-white/20 my-3" />
      <div
        className="flex flex-col items-center gap-2 w-full"
        role="tablist"
        aria-label="App mode tabs"
      >
        {items.map((item) => {
          const isActive = item.modeId === mode;
          return (
            <Link
              key={item.modeId}
              to={item.to}
              role="tab"
              aria-selected={isActive}
              className={`group relative flex flex-col items-center justify-center gap-1 w-14 py-2 rounded-lg text-[10px] font-medium transition-all duration-200 ${
                isActive
                  ? 'bg-white/15 text-white'
                  : 'text-indigo-100 hover:bg-white/10 hover:text-white'
              }`}
            >
              {isActive && (
                <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-gradient-to-b from-white to-indigo-200" />
              )}
              <span className={isActive ? 'text-white' : 'text-indigo-200 group-hover:text-white'}>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </aside>
  );
}

function visibleModeRailItems(videoVisible: boolean): ModeRailItem[] {
  return videoVisible
    ? modeRailItems
    : modeRailItems.filter((item) => item.modeId !== 'video');
}

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { health: lightningHealth, beLive: lightningBeLive, dbLive: lightningDbLive } =
    useHealth();
  // The video health hook is always mounted (even in lightning mode) so
  // we can show its counts immediately on a tab switch without a 5s
  // delay. When the video service isn't enabled at build time, video
  // requests still go out but get a 4xx/network error and the hook just
  // reports beLive=false, which is harmless because we only consume it
  // while `mode === 'video'`.
  const { health: videoHealth, beLive: videoBeLive } = useVideoHealth();
  const { authBypassActive } = useAuth();
  const location = useLocation();
  const mode = detectMode(location.pathname);
  const videoVisible = isVideoEnabled();
  const navItems = mode === 'video' ? videoNavItems : lightningNavItems;
  const homeLink = mode === 'video' ? '/video/overview' : '/lightning/overview';
  // In lightning mode the "Generate" button is a route link to a full
  // page. In video mode it opens an inline modal (mirroring the original
  // 360_video UX, which never had a /generate page).
  const generateLink = mode === 'video' ? null : '/lightning/generate';
  const [videoGenerateOpen, setVideoGenerateOpen] = useState(false);

  // Header pills follow the active mini-app: lightning's flat counts vs
  // the video service's nested `counts` object. Each backend reports
  // only its own jobs, so switching tabs swaps the numbers.
  const counts: SystemStatusCounts | null =
    mode === 'video'
      ? videoHealth
        ? {
            active: videoHealth.counts.processing,
            pending: videoHealth.counts.pending,
            completed: videoHealth.counts.completed,
            failed: videoHealth.counts.failed,
          }
        : null
      : lightningHealth
        ? {
            active: lightningHealth.active_jobs,
            pending: lightningHealth.pending_jobs,
            completed: lightningHealth.completed_jobs,
            failed: lightningHealth.failed_jobs,
          }
        : null;

  const beLive = mode === 'video' ? videoBeLive : lightningBeLive;
  // The video service doesn't currently expose a separate DB-liveness
  // signal on `/health`, so in video mode we only show the BE indicator.
  const showDbIndicator = mode !== 'video';
  const dbLive = lightningDbLive;
  const beVersion =
    mode === 'video' ? videoHealth?.version : lightningHealth?.version;
  const localBuild = mode === 'video' ? false : Boolean(lightningHealth?.local_build);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex">
      {/* Mode rail */}
      <ModeRail mode={mode} videoVisible={videoVisible} homeLink={homeLink} />

      {/* Sidebar */}
      <aside className="hidden md:flex w-52 shrink-0 flex-col border-r border-slate-200 bg-white sticky top-0 h-screen self-start z-30">
        <nav className="px-3 pt-4 flex-1 overflow-y-auto">
          {generateLink && (
            <>
              <div className="flex justify-center">
                <Link
                  to={generateLink}
                  className="inline-flex items-center justify-center gap-2 px-4 h-10 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium text-sm shadow-md hover:shadow-lg hover:opacity-95 transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Generate Scans
                </Link>
              </div>
              <div className="my-3 border-t border-slate-200" />
            </>
          )}
          {mode === 'video' && (
            <>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => setVideoGenerateOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-4 h-10 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium text-sm shadow-md hover:shadow-lg hover:opacity-95 transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Generate Scan
                </button>
              </div>
              <div className="my-3 border-t border-slate-200" />
            </>
          )}
          <div className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                    isActive
                      ? 'bg-indigo-50 text-indigo-600 font-medium'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
                    )}
                    <span className={isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}>
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="px-4 py-4 border-t border-slate-200 text-[11px] text-slate-500 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap font-mono text-slate-400">
            {__GIT_BRANCH__ !== 'unknown' && (
              <a
                href={`${GITHUB_REPO_URL}/tree/${__GIT_BRANCH__}`}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate max-w-[8rem] hover:text-indigo-600"
                title={`Branch: ${__GIT_BRANCH__}`}
              >
                {__GIT_BRANCH__}
              </a>
            )}
            {__GIT_BRANCH__ !== 'unknown' && __GIT_COMMIT__ !== 'unknown' && (
              <span className="text-slate-300">@</span>
            )}
            {__GIT_COMMIT__ !== 'unknown' && (
              <a
                href={`${GITHUB_REPO_URL}/commit/${__GIT_COMMIT__}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-indigo-600"
                title={`Commit: ${__GIT_COMMIT__}`}
              >
                {__GIT_COMMIT__.slice(0, 7)}
              </a>
            )}
            {__GIT_BRANCH__ === 'unknown' && __GIT_COMMIT__ === 'unknown' && (
              <span className="italic text-slate-400">git info unavailable</span>
            )}
            {localBuild && (
              <span
                className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold tracking-wider bg-amber-50 text-amber-600"
                title="Running a local `make deploy` build, not a CI deployment"
              >
                LOCAL
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center gap-1"
              title={beLive ? 'Backend service is responding' : 'Backend service is not reachable'}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  beLive ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                }`}
              />
              <span className={beLive ? 'text-green-600' : 'text-red-600'}>
                BE {beLive ? 'online' : 'offline'}
              </span>
            </span>
            {showDbIndicator && (
              <span
                className="inline-flex items-center gap-1"
                title={dbLive ? 'Database is reachable' : 'Database is not reachable'}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    dbLive ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                  }`}
                />
                <span className={dbLive ? 'text-green-600' : 'text-red-600'}>
                  DB {dbLive ? 'online' : 'offline'}
                </span>
              </span>
            )}
          </div>
          {authBypassActive && (
            <div>
              <span
                className="inline-flex px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-medium"
                title="Set VITE_SKIP_AUTH=false to require Google sign-in"
              >
                Dev bypass
              </span>
            </div>
          )}
          <div className="font-mono text-slate-400">v{__APP_VERSION__}</div>
          {beVersion && (
            <div className="font-mono text-slate-400">be v{beVersion}</div>
          )}
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-20">
          <div className="px-6 py-2 flex items-center gap-4 flex-wrap">
            <div className="flex-1 flex justify-start min-w-0">
              <SystemStatusHeader counts={counts} mode={mode} />
            </div>

            <div className="flex items-center gap-4 shrink-0 justify-end">
              <div className="hidden lg:block">
                <Login />
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-6 py-6">{children}</main>
      </div>

      {/* Video-only "Generate Scan" modal lives at the shell level so it
          can be triggered from the header on any /video/* page. */}
      {mode === 'video' && (
        <GenerateScanModal
          open={videoGenerateOpen}
          onClose={() => setVideoGenerateOpen(false)}
          onSubmitted={() => {
            window.dispatchEvent(new CustomEvent(VIDEO_SCAN_SUBMITTED_EVENT));
          }}
        />
      )}
    </div>
  );
}
